import React, { useState, useEffect } from "react";
import { checklistApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  CheckCircle2,
  FileText,
  Upload,
  Hash,
  Calendar,
  ListChecks,
  Link as LinkIcon,
  Mail,
  AlignLeft,
  Loader2,
  Save,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// Field type icons
const FIELD_ICONS = {
  checkbox: CheckCircle2,
  text: FileText,
  number: Hash,
  dropdown: ListChecks,
  file: Upload,
  date: Calendar,
  textarea: AlignLeft,
  url: LinkIcon,
  email: Mail,
};

/**
 * DynamicChecklist - renders a checklist from a template
 * 
 * @param {string} stage - "sales" | "project_start" | "task"
 * @param {string} target - "internal" | "client_request" (default: "internal")
 * @param {string} serviceType - optional filter by service type
 * @param {string} entityId - project or task ID
 * @param {string} entityType - "project" | "task"
 * @param {function} onComplete - callback when checklist is saved
 * @param {boolean} disabled - if true, form is read-only
 * @param {boolean} defaultCollapsed - start collapsed
 */
const DynamicChecklist = ({
  stage,
  target = "internal",
  serviceType,
  entityId,
  entityType = "project",
  onComplete,
  disabled = false,
  defaultCollapsed = false,
  title,
}) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [instance, setInstance] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  useEffect(() => {
    fetchTemplates();
  }, [stage, target, serviceType]);

  useEffect(() => {
    if (entityId && selectedTemplate) {
      fetchInstance();
    }
  }, [entityId, selectedTemplate]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      // For task stage, show templates regardless of target (can be used to track requirements)
      const params = { stage };
      if (stage !== "task") {
        params.target = target;
      }
      const res = await checklistApi.getTemplates(params);
      let filtered = res.data || [];

      // Filter by service type if provided
      if (serviceType) {
        // Normalize service type for matching (case-insensitive)
        const normalizedServiceType = serviceType.toLowerCase();
        filtered = filtered.filter(
          (t) => t.service_types?.length === 0 || 
                 t.service_types?.some(st => st.toLowerCase() === normalizedServiceType)
        );
      }

      setTemplates(filtered);

      // Auto-select first template
      if (filtered.length > 0) {
        setSelectedTemplate(filtered[0]);
        initFieldValues(filtered[0]);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInstance = async () => {
    try {
      const params = {
        [entityType === "project" ? "project_id" : "task_id"]: entityId,
        template_id: selectedTemplate?.id,
      };
      const res = await checklistApi.getInstances(params);
      const instances = res.data || [];

      if (instances.length > 0) {
        setInstance(instances[0]);
        // Load saved values
        const values = {};
        instances[0].fields?.forEach((f) => {
          values[f.field_id] = f.value;
        });
        setFieldValues(values);
      } else {
        // No existing instance - initialize with defaults
        initFieldValues(selectedTemplate);
      }
    } catch (error) {
      console.error("Error fetching instance:", error);
    }
  };

  const initFieldValues = (template) => {
    const values = {};
    template?.fields?.forEach((field) => {
      values[field.id] = field.default_value || (field.field_type === "checkbox" ? false : "");
    });
    setFieldValues(values);
  };

  const updateFieldValue = (fieldId, value) => {
    setFieldValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleSave = async () => {
    if (!selectedTemplate || !entityId) return;

    setSaving(true);
    try {
      // Convert fieldValues to array format
      const fieldsArray = Object.entries(fieldValues).map(([fieldId, value]) => ({
        field_id: fieldId,
        value: value,
      }));

      if (instance) {
        // Update existing instance
        await checklistApi.updateInstance(instance.id, { fields: fieldsArray });
        toast.success("Checklist saved");
      } else {
        // Create new instance
        const data = {
          template_id: selectedTemplate.id,
          [entityType === "project" ? "project_id" : "task_id"]: entityId,
          fields: fieldsArray,
        };
        const res = await checklistApi.createInstance(data);
        setInstance(res.data);
        toast.success("Checklist created");
      }

      // Check completion and call callback
      const completionStatus = calculateCompletion();
      if (onComplete) {
        onComplete({
          is_complete: completionStatus.isComplete,
          completed: completionStatus.completed,
          required: completionStatus.required,
        });
      }
    } catch (error) {
      console.error("Error saving checklist:", error);
      toast.error("Failed to save checklist");
    } finally {
      setSaving(false);
    }
  };

  const calculateCompletion = () => {
    if (!selectedTemplate) return { isComplete: false, completed: 0, required: 0 };

    const requiredFields = selectedTemplate.fields?.filter((f) => f.is_required) || [];
    let completedRequired = 0;

    requiredFields.forEach((field) => {
      const value = fieldValues[field.id];
      if (field.field_type === "checkbox") {
        if (value === true || value === "true") completedRequired++;
      } else if (value && String(value).trim()) {
        completedRequired++;
      }
    });

    return {
      isComplete: completedRequired === requiredFields.length,
      completed: completedRequired,
      required: requiredFields.length,
    };
  };

  const renderField = (field) => {
    const Icon = FIELD_ICONS[field.field_type] || FileText;
    const value = fieldValues[field.id] ?? "";
    const isCompleted = field.field_type === "checkbox" 
      ? (value === true || value === "true")
      : Boolean(value && String(value).trim());

    return (
      <div
        key={field.id}
        className={cn(
          "p-3 rounded-lg border transition-all",
          isCompleted && field.is_required
            ? "bg-green-500/5 border-green-500/20"
            : "bg-zinc-800/30 border-zinc-700/50"
        )}
      >
        <div className="space-y-2">
          <Label className="text-zinc-300 flex items-center gap-2">
            <Icon className={cn("h-4 w-4", isCompleted ? "text-green-400" : "text-zinc-500")} />
            {field.label}
            {field.is_required && <span className="text-red-400">*</span>}
            {isCompleted && field.is_required && (
              <CheckCircle2 className="h-3.5 w-3.5 text-green-400 ml-1" />
            )}
          </Label>

          {field.field_type === "checkbox" && (
            <div className="flex items-center gap-2">
              <Checkbox
                checked={value === true || value === "true"}
                onCheckedChange={(checked) => updateFieldValue(field.id, checked)}
                disabled={disabled}
                className={cn("border-zinc-600", isCompleted && "border-green-500 data-[state=checked]:bg-green-500")}
              />
              <span className="text-sm text-zinc-400">{field.help_text || "Check if complete"}</span>
            </div>
          )}

          {field.field_type === "text" && (
            <Input
              value={value}
              onChange={(e) => updateFieldValue(field.id, e.target.value)}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
              disabled={disabled}
              className="bg-zinc-800 border-zinc-700 text-zinc-50"
            />
          )}

          {field.field_type === "number" && (
            <Input
              type="number"
              value={value}
              onChange={(e) => updateFieldValue(field.id, e.target.value)}
              placeholder={field.placeholder || "0"}
              disabled={disabled}
              className="bg-zinc-800 border-zinc-700 text-zinc-50"
            />
          )}

          {field.field_type === "dropdown" && (
            <Select
              value={value}
              onValueChange={(v) => updateFieldValue(field.id, v)}
              disabled={disabled}
            >
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {field.options?.map((opt) => (
                  <SelectItem key={opt.value || opt} value={opt.value || opt}>
                    {opt.label || opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {field.field_type === "textarea" && (
            <Textarea
              value={value}
              onChange={(e) => updateFieldValue(field.id, e.target.value)}
              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
              disabled={disabled}
              className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
            />
          )}

          {field.field_type === "date" && (
            <Input
              type="date"
              value={value}
              onChange={(e) => updateFieldValue(field.id, e.target.value)}
              disabled={disabled}
              className="bg-zinc-800 border-zinc-700 text-zinc-50"
            />
          )}

          {field.field_type === "url" && (
            <Input
              type="url"
              value={value}
              onChange={(e) => updateFieldValue(field.id, e.target.value)}
              placeholder={field.placeholder || "https://..."}
              disabled={disabled}
              className="bg-zinc-800 border-zinc-700 text-zinc-50"
            />
          )}

          {field.field_type === "email" && (
            <Input
              type="email"
              value={value}
              onChange={(e) => updateFieldValue(field.id, e.target.value)}
              placeholder={field.placeholder || "email@example.com"}
              disabled={disabled}
              className="bg-zinc-800 border-zinc-700 text-zinc-50"
            />
          )}

          {field.field_type === "file" && (
            <div className="space-y-2">
              <Input
                type="url"
                value={value}
                onChange={(e) => updateFieldValue(field.id, e.target.value)}
                placeholder="Paste link to file (Google Drive, Dropbox, etc.)"
                disabled={disabled}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
              <p className="text-xs text-zinc-500">Upload file to cloud storage and paste the link</p>
            </div>
          )}

          {field.help_text && field.field_type !== "checkbox" && (
            <p className="text-xs text-zinc-500">{field.help_text}</p>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
        </CardContent>
      </Card>
    );
  }

  if (templates.length === 0) {
    return null; // No templates for this stage
  }

  const completion = calculateCompletion();
  const displayTitle = title || selectedTemplate?.name || "Checklist";

  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader 
        className="pb-3 cursor-pointer" 
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-zinc-50 font-['Manrope'] flex items-center gap-2">
            <ListChecks className={cn("h-5 w-5", completion.isComplete ? "text-green-400" : "text-orange-500")} />
            {displayTitle}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={cn(
                "text-xs",
                completion.isComplete
                  ? "bg-green-500/10 text-green-400 border-green-500/30"
                  : "bg-orange-500/10 text-orange-400 border-orange-500/30"
              )}
            >
              {completion.completed}/{completion.required} Required
            </Badge>
            {collapsed ? (
              <ChevronDown className="h-4 w-4 text-zinc-500" />
            ) : (
              <ChevronUp className="h-4 w-4 text-zinc-500" />
            )}
          </div>
        </div>
        {selectedTemplate?.description && !collapsed && (
          <p className="text-sm text-zinc-500 mt-1">{selectedTemplate.description}</p>
        )}
      </CardHeader>

      {!collapsed && (
        <CardContent className="space-y-3">
          {/* Template selector if multiple */}
          {templates.length > 1 && (
            <div className="flex flex-wrap gap-2 pb-3 border-b border-zinc-800">
              {templates.map((template) => (
                <Badge
                  key={template.id}
                  variant="outline"
                  onClick={() => {
                    setSelectedTemplate(template);
                    initFieldValues(template);
                    setInstance(null);
                  }}
                  className={cn(
                    "cursor-pointer transition-all",
                    selectedTemplate?.id === template.id
                      ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/50"
                      : "bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-cyan-500/30"
                  )}
                >
                  {template.name}
                </Badge>
              ))}
            </div>
          )}

          {/* Fields */}
          {selectedTemplate?.fields?.map(renderField)}

          {/* Completion message */}
          {completion.isComplete && (
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-400" />
              <span className="text-sm text-green-300">All required items complete!</span>
            </div>
          )}

          {/* Save button */}
          {!disabled && entityId && (
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {saving ? "Saving..." : "Save Checklist"}
              </Button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default DynamicChecklist;
